import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useUpsertCampaign, type Campaign } from './useCampaigns';

const PLATFORMS = ['meta', 'google', 'tiktok', 'other'] as const;

export function CampaignDialog({
  open,
  onClose,
  campaign,
}: {
  open: boolean;
  onClose: () => void;
  campaign?: Campaign | undefined;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertCampaign();

  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('meta');
  const [objective, setObjective] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyBudget, setDailyBudget] = useState('');
  const [areas, setAreas] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(campaign?.name ?? '');
    setPlatform(campaign?.platform ?? 'meta');
    setObjective(campaign?.objective ?? '');
    setExternalRef(campaign?.external_ref ?? '');
    setStartDate(campaign?.start_date ?? '');
    setEndDate(campaign?.end_date ?? '');
    setDailyBudget(campaign?.daily_budget ?? '');
    setAreas((campaign?.target_areas ?? []).join('، '));
    setError(null);
  }, [open, campaign]);

  const submit = async () => {
    if (!name.trim()) {
      setError(t('campaigns.nameRequired'));
      return;
    }
    try {
      await upsert.mutateAsync({
        id: campaign?.id ?? null,
        name: name.trim(),
        platform,
        objective: objective.trim() || null,
        externalRef: externalRef.trim() || null,
        startDate: startDate || null,
        endDate: endDate || null,
        dailyBudget: dailyBudget.trim() ? Number(dailyBudget) : null,
        targetAreas: areas
          .split(/[,،]/)
          .map((a) => a.trim())
          .filter(Boolean),
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal
      open={open}
      title={campaign ? t('campaigns.edit') : t('campaigns.create')}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Input label={t('campaigns.name')} value={name} onChange={(e) => setName(e.target.value)} />

        <Select
          label={t('campaigns.platform')}
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          options={PLATFORMS.map((p) => ({
            value: p,
            label: t(`platforms.${p}` as 'platforms.meta'),
          }))}
        />

        <Input
          label={t('campaigns.objective')}
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
        />

        {/* The platform's own campaign id. The Meta webhook matches on this, so a
            lead can find its campaign without anyone keeping a mapping table. */}
        <Input
          label={t('campaigns.externalRef')}
          hint={t('campaigns.externalRefHint')}
          dir="ltr"
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('campaigns.startDate')}
            type="date"
            dir="ltr"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label={t('campaigns.endDate')}
            type="date"
            dir="ltr"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <Input
          label={t('campaigns.dailyBudget')}
          type="number"
          min="0"
          dir="ltr"
          value={dailyBudget}
          onChange={(e) => setDailyBudget(e.target.value)}
        />

        <Input
          label={t('campaigns.targetAreas')}
          hint={t('inventory.areasHint')}
          value={areas}
          onChange={(e) => setAreas(e.target.value)}
        />

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={upsert.isPending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
